import BaseApi from './base.api';

// ==================== Types (mirror NhisMonitoringService DTOs) ====================

export interface MonitoringOverview {
  bridgeOnline: boolean;
  lastSeen: string | null;
  openCircuits: number;
  queued: number;
  claimed: number;
  completed: number;
  failed: number;
  errors24h: number;
}

export interface BridgeHealth {
  bridgeId: string;
  lastSeen: string | null;
  online: boolean;
  version: string | null;
  kepCertExpiresAt: string | null;
  certStatus: 'VALID' | 'EXPIRING' | 'EXPIRED' | 'UNKNOWN';
}

export interface CircuitStatus {
  bridgeId: string;
  flow: string;
  state: string; // CLOSED | OPEN
  trippedAt: string | null;
  tripReason: string | null;
  lastHttpStatus: number | null;
}

export interface ErrorRow {
  callLogId: string;
  attemptedAt: string | null;
  patient: string;
  syncType: string | null;
  httpStatus: number | null;
  businessErrorType: string | null;
  message: string | null;
}

export interface ErrorBody {
  callLogId: string;
  requestBody: string | null;
  responseBody: string | null;
  createdAt: string | null;
}

export interface FailedJob {
  jobId: string;
  patient: string;
  syncType: string | null;
  apiSurface: string | null;
  nextAttemptAt: string | null;
  createdAt: string | null;
}

export interface MonitoringSnapshot {
  overview: MonitoringOverview;
  bridges: BridgeHealth[];
  circuits: CircuitStatus[];
  errors: ErrorRow[];
  failedJobs: FailedJob[];
}

// ==================== API ====================

class NhisApi extends BaseApi {
  /** Full bridge-monitoring snapshot for the admin dashboard. */
  getMonitoring = (): Promise<MonitoringSnapshot> => this.get('/admin/nhis/monitoring');

  /** Re-queue a single failed sync job. */
  retryJob = (jobId: string): Promise<{ ok: boolean }> =>
    this.post(`/admin/nhis/jobs/${jobId}/retry`);

  /** Reset a tripped circuit breaker for a bridge flow. */
  resetCircuit = (bridgeId: string, flow: string): Promise<{ ok: boolean }> =>
    this.post(`/admin/nhis/bridges/${bridgeId}/circuit/${flow}/reset`);

  /** Raw request/response body for a single error (admin drill-in). */
  getErrorBody = (callLogId: string): Promise<ErrorBody> =>
    this.get(`/admin/nhis/errors/${callLogId}/body`);

  /** Mark an error as reviewed (resolved) — removes it from the board. */
  reviewError = (callLogId: string, note?: string): Promise<{ ok: boolean }> =>
    this.post(`/admin/nhis/errors/${callLogId}/review`, note ? { note } : {});
}

export const nhisApi = new NhisApi();
export default nhisApi;
